/**
 * The time, ticking once a second, from one shared timer however many
 * components show it (the place page's "updated Ns ago" lines, and its "Undo"
 * going when its five minutes are up). The timer runs only while something is
 * subscribed. The value changes only when the timer starts or ticks, so every
 * read between two ticks agrees.
 */
import * as React from "react";

const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let now = Date.now();

function snapshot(): number {
  return now;
}

/** Listen for each tick; the one timer starts with the first listener and
 *  stops with the last. Returns the way to stop listening. */
export function subscribeToNow(listener: () => void): () => void {
  listeners.add(listener);
  if (timer === null) {
    // The time may be old (no timer since); React reads it again after
    // subscribing and re-renders when it moved.
    now = Date.now();
    timer = setInterval(() => {
      now = Date.now();
      for (const l of listeners) l();
    }, 1000);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}

/** Listens for nothing: `useNow` while nothing on screen needs the tick. */
function noTicks(): () => void {
  return () => {};
}

/** The time now (ms), re-rendering once a second; while `active` is false it
 *  neither listens nor re-renders (the time it returns may then be old). */
export function useNow(active = true): number {
  return React.useSyncExternalStore(active ? subscribeToNow : noTicks, snapshot, snapshot);
}
